import * as React from 'react'

import {
  type BlockChange,
  type ListBlock,
  type Block,
  type ComponentBlock,
  type InstanceBlock,
  isInstanceBlock,
  isComponentBlock,
} from '@basehub/data/replicache/index'

import { type TreeManager } from '@basehub/utils/tree/index'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { useListTable } from './use-list-table'
import {
  ArrowDownIcon,
  ArrowUpIcon,
  MinusIcon,
  InfoCircledIcon,
  LetterCaseCapitalizeIcon,
  ArrowTopRightIcon,
} from '@radix-ui/react-icons'
import { ListBlockViewIcon } from './views/view-switcher'
import { Button } from '~/sotano-kit/primitives/button'
import { StatusIcon } from '~/components/icons/status'
import { ArrowUpDownIcon } from './views/table/sort'
import {
  LIST_BLOCK_DARK_THEME,
  LIST_BLOCK_LIGHT_THEME,
  ROW_HEIGHT,
} from './views/table/theme'
import { FunnelIcon } from '~/components/icons/funnel'
import { Tabs, TabsList, TabsTrigger } from '~/sotano-kit/primitives/tabs'
import { useSelectedView } from './views/use-selected-view'
import { getViewDefaultTitle } from '@basehub/data/replicache/schema/block/list/utils'
import { clsx } from 'clsx'
import { BlockIcon } from '~/components/block-icon'
import { InlineTextDiff } from '../rich-text/diff'
import { useGetTabPrefix, useTabsStore } from '../../tabs/store'
import { useTheme } from 'next-themes'
import styles from './table-diff.module.scss'

export const ListBlockDiff: React.FC<{
  condition: 'removed' | 'added'
  blockChange: BlockChange
  treeManager: TreeManager<Block, NonNullable<unknown>> | null
  fullView: boolean
}> = ({ condition, blockChange, treeManager }) => {
  const diff = React.useMemo(
    () =>
      (blockChange.children ?? []).map((child) => {
        const correspondingTreeId =
          condition === 'added'
            ? child.id
            : blockChange.previous
              ? treeManager
                  ?.getChildrenKeys(idToSourceBlockId(blockChange.previous))
                  ?.find((childId) => {
                    const node = treeManager?.getNode(childId)?.value
                    return node?.sourceBlockId === child.id
                  })
              : child.id

        return { id: correspondingTreeId ?? child.id, status: child.status }
      }),
    [blockChange.children, blockChange.previous, condition, treeManager]
  )
  const tabPrefix = useGetTabPrefix()
  const addTab = useTabsStore((s) => s.addTab)
  const theme =
    useTheme().resolvedTheme === 'dark'
      ? LIST_BLOCK_DARK_THEME
      : LIST_BLOCK_LIGHT_THEME

  const block = (
    condition === 'added' ? blockChange.current : blockChange.previous
  ) as ListBlock
  const oppositeBlock = (
    condition === 'added' ? blockChange.previous : blockChange.current
  ) as ListBlock
  const node = block?.id ? treeManager?.getNode(block.id) : null
  const parentNode = node?.parentKey
    ? treeManager?.getNode(node.parentKey)
    : null
  const [selectedView] = useSelectedView(block)

  const { rows } = useListTable({
    block,
    treeManager,
  })

  const getRowsAndAccordions = React.useCallback(() => {
    const addedRowsMap = new Set<string>()
    const rowsInFilters = rows.reduce<
      (
        | { type: 'accordion'; rowCount: number }
        | {
            type: 'row'
            row: InstanceBlock | ComponentBlock
            matchesFilters: boolean
            status: NonNullable<BlockChange['children']>[number]['status']
          }
      )[]
    >((acc, r) => {
      const match = (diff ?? []).find((diffItem) => {
        if (condition === 'removed') return diffItem.id === r.id

        return diffItem.id === idToSourceBlockId(r)
      })

      const appendToLastAccordion = () => {
        if (acc[acc.length - 1]?.type === 'accordion') {
          const last = acc.pop()
          if (last && last.type === 'accordion') {
            acc.push({ type: 'accordion', rowCount: last.rowCount + 1 })
          }
        } else {
          acc.push({ type: 'accordion', rowCount: 1 })
        }
      }

      switch (match?.status) {
        case 'created':
          {
            if (condition === 'added') {
              acc.push({
                type: 'row',
                status: 'created',
                row: r,
                matchesFilters: true,
              })
              addedRowsMap.add(idToSourceBlockId(r))
            }
          }
          break
        case 'deleted':
          {
            if (condition === 'removed') {
              acc.push({
                type: 'row',
                status: 'deleted',
                row: r,
                matchesFilters: true,
              })
              addedRowsMap.add(idToSourceBlockId(r))
            }
          }
          break
        case 'modified': {
          if (condition === 'removed') {
            appendToLastAccordion()
            return acc
          }
          acc.push({
            type: 'row',
            status: 'modified',
            row: r,
            matchesFilters: true,
          })
          addedRowsMap.add(idToSourceBlockId(r))
          break
        }
        case 'reordered-source':
        case 'reordered': {
          acc.push({
            type: 'row',
            status: 'reordered',
            row: r,
            matchesFilters: true,
          })
          addedRowsMap.add(idToSourceBlockId(r))
          break
        }
        default:
          {
            if (acc[acc.length - 1]?.type === 'accordion') {
              const last = acc.pop()
              if (last && last.type === 'accordion') {
                acc.push({ type: 'accordion', rowCount: last.rowCount + 1 })
              }
            } else {
              acc.push({ type: 'accordion', rowCount: 1 })
            }
            addedRowsMap.add(idToSourceBlockId(r))
          }
          break
      }

      return acc
    }, [])

    ;(blockChange.children ?? []).forEach((child) => {
      if (addedRowsMap.has(child.id)) return
      const row = treeManager?.getNode(child.id)?.value
      if (!row) return
      if (!isInstanceBlock(row) && !isComponentBlock(row)) return
      if (addedRowsMap.has(idToSourceBlockId(row))) return
      if (child.status === 'modified' && condition === 'removed') return
      rowsInFilters.push({
        type: 'row',
        status: child.status,
        row,
        matchesFilters: false,
      })
    })

    return rowsInFilters
  }, [blockChange.children, condition, diff, rows, treeManager])
  const getRowsAndAccordionsRef = React.useRef(getRowsAndAccordions)
  getRowsAndAccordionsRef.current = getRowsAndAccordions

  const [rowsWithAccordions, setRowsWithAccordions] =
    React.useState(getRowsAndAccordions)

  React.useEffect(() => {
    setRowsWithAccordions(getRowsAndAccordionsRef.current)
  }, [selectedView])

  if (!block || !parentNode) return null

  return (
    <div className="text-xs">
      <ListBlockViewsDiff
        block={block}
        oppositeBlock={oppositeBlock}
        condition={condition}
        getNode={treeManager?.getNode}
      />

      <div
        style={{ height: ROW_HEIGHT }}
        className="flex border-y border-dark-border-solid bg-dark-base-dark text-dark-bright font-medium"
      >
        <p className="flex-0 gap-x-2 basis-24 flex items-center border-r border-dark-border-solid px-3">
          <InfoCircledIcon />
          Status
        </p>
        <p className="flex-1 flex items-center gap-x-2 px-3">
          <LetterCaseCapitalizeIcon />
          Title
        </p>
      </div>

      {rowsWithAccordions.map((r, i) => {
        if (r.type === 'accordion') {
          let accordionPosition = 'middle'
          let icon = (
            <ArrowUpDownIcon width={15} height={15} className="size-[15px]" />
          )
          if (i === 0 && i === rowsWithAccordions.length - 1) {
            icon = <ArrowDownIcon className="size-[15px]" />
            accordionPosition = 'top'
          } else if (i === 0) {
            icon = <ArrowUpIcon className="size-[15px]" />
            accordionPosition = 'top'
          } else if (i === rowsWithAccordions.length - 1) {
            icon = <ArrowDownIcon className="size-[15px]" />
            accordionPosition = 'bottom'
          }

          return (
            <Button
              unstyled
              focusStyled
              key={i}
              style={{ height: ROW_HEIGHT }}
              className="flex items-center border-b border-dark-border-solid cursor-default hover:bg-dark-shade rounded-none w-full text-dark-faint font-medium gap-x-2 px-3"
              onClick={() => {
                setRowsWithAccordions((prev) => {
                  const next = [...prev]
                  const rowsCopy = [...rows]
                  const previousRow = rowsWithAccordions[i - 1]
                  if (!!previousRow && previousRow.type === 'accordion') {
                    return prev
                  }
                  const indexOfFirstRowForThisAccordion = !previousRow
                    ? 0
                    : rows.findIndex((r) => r.id === previousRow.row.id) + 1

                  const rowsForThisAccordion = rowsCopy
                    .splice(indexOfFirstRowForThisAccordion, r.rowCount)
                    .map(
                      (r) =>
                        ({
                          type: 'row',
                          status: 'unmodified',
                          row: r,
                          matchesFilters: true,
                        }) as const
                    )

                  next.splice(i, 1, ...rowsForThisAccordion)
                  return next
                })
              }}
            >
              {icon}
              <span className="flex-1 text-left">
                Show {r.rowCount} unmodified rows{' '}
                {accordionPosition === 'middle'
                  ? 'in between'
                  : accordionPosition === 'top'
                    ? 'above'
                    : 'below'}
              </span>
            </Button>
          )
        }

        return (
          <div
            key={i}
            style={{
              height: ROW_HEIGHT,
            }}
            data-condition={
              r.status === 'modified' && condition === 'removed'
                ? undefined
                : r.status
            }
            className={clsx(
              'flex items-center border-b border-dark-border-solid text-dark-muted',
              styles.tableDiff
            )}
          >
            <div className="w-24 h-full flex items-center px-3 border-r border-dark-border-solid mr-3 gap-x-2">
              <StatusIcon
                className="w-4"
                status={
                  r.status === 'reordered-source' ? 'reordered' : r.status
                }
                blameProps={{
                  sourceBlockId: idToSourceBlockId(r.row),
                  rootId: treeManager?.root.value.id ?? null,
                }}
              />
              {selectedView?.filter.length ? (
                <FunnelIcon
                  className={clsx('w-4', !r.matchesFilters && 'opacity-30')}
                />
              ) : (
                <MinusIcon className="w-4" />
              )}
            </div>

            <span
              className={clsx(
                'truncate flex-1 flex items-center w-full group',
                r.row.id === block.typeOptions.targetBlockId &&
                  'text-dark-control-base'
              )}
            >
              <BlockIcon
                block={r.row}
                iconProps={{
                  className: clsx(r.row.hidden && 'opacity-30'),
                }}
              />
              &nbsp;&nbsp;
              <span className={clsx(r.row.hidden && 'opacity-30')}>
                {r.row.title || 'Untitled'}
              </span>
              <Button
                unstyled
                focusStyled
                style={{
                  ['--bg-color' as string]: theme.bgHeader,
                  ['--bg-color-hover' as string]: theme.bgHeaderHovered,
                  ['--border-color' as string]: theme.bgBubble,
                  ['--border-color-hover' as string]:
                    theme.headerBottomBorderColor,
                }}
                className="cursor-default size-6 mr-2 invisible group-hover:visible rounded-md ml-auto border border-[var(--border-color)] text-dark-bright flex items-center justify-center hover:border-[var(--border-color-hover)] hover:bg-[var(--bg-color-hover)] bg-[var(--bg-color)]"
                onClick={(e) => {
                  const tabKey =
                    r.status === 'unmodified'
                      ? idToSourceBlockId(r.row)
                      : `${tabPrefix}${idToSourceBlockId(r.row)}`

                  if (e.metaKey) return window.open(tabKey)
                  addTab({
                    key: tabKey,
                    partiallyOpen: true,
                  })
                }}
              >
                <ArrowTopRightIcon className="size-[14px]" />
              </Button>
            </span>
          </div>
        )
      })}
    </div>
  )
}

const ListBlockViewsDiff = ({
  condition,
  block,
  oppositeBlock,
  getNode,
}: {
  condition: 'added' | 'removed'
  block: ListBlock
  oppositeBlock: ListBlock | null
  getNode: TreeManager<Block, NonNullable<unknown>>['getNode'] | undefined
}) => {
  const [selectedView, updateSelectedView] = useSelectedView(block)
  const [oppositeView] = useSelectedView(oppositeBlock ?? block)

  const { currentFilters, oppositeFilters } = React.useMemo(() => {
    if (!getNode) return { currentFilters: '', oppositeFilters: '' }

    const current = selectedView?.filter
      .map((f) => {
        if (f.type === 'and' || f.type === 'or') {
          throw new Error('and/or not supported yet')
        }
        const titleOfThisFilter =
          f.by === '__title' ? 'Title' : (getNode(f.by)?.value.title ?? '')

        return `${titleOfThisFilter} ${f.type} "${f.value}"`
      })
      .join(', ')

    const opposite = oppositeView?.filter
      .map((f) => {
        if (f.type === 'and' || f.type === 'or') {
          throw new Error('and/or not supported yet')
        }
        const titleOfThisFilter =
          f.by === '__title' ? 'Title' : (getNode(f.by)?.value.title ?? '')

        return `${titleOfThisFilter} ${f.type} "${f.value}"`
      })
      .join(', ')

    return { currentFilters: current, oppositeFilters: opposite }
  }, [getNode, selectedView?.filter, oppositeView?.filter])

  const { currentSort, oppositeSort } = React.useMemo(() => {
    if (!getNode) return { currentSort: '', oppositeSort: '' }

    const current = selectedView?.sort
      .map((f) => {
        const titleOfThisSortingMethod =
          f.by === '__title' ? 'Title' : (getNode(f.by)?.value.title ?? '')

        return `${titleOfThisSortingMethod} ${f.type}`
      })
      .join(', ')
    const opposite = oppositeView?.sort
      .map((f) => {
        const titleOfThisSortingMethod =
          f.by === '__title' ? 'Title' : (getNode(f.by)?.value.title ?? '')

        return `${titleOfThisSortingMethod} ${f.type}`
      })
      .join(', ')

    return { currentSort: current, oppositeSort: opposite }
  }, [getNode, selectedView?.sort, oppositeView?.sort])

  return (
    <>
      <Tabs
        className="flex items-center gap-2 overflow-x-auto no-scrollbar mb-3"
        value={selectedView?.id ?? ''}
        onValueChange={(value) => {
          if (!(block.value.views ?? []).length) return
          updateSelectedView(value)
        }}
      >
        <TabsList className="!p-0 overflow-hidden !justify-start" loop={false}>
          {block.value.views?.map((view) => {
            let status: BlockChange['status'] = 'unmodified'
            if (!oppositeBlock) {
              if (condition === 'added') {
                status = 'created'
              }
            } else if (
              !oppositeBlock.value.views?.find((v) => v.id === view.id)
            ) {
              if (condition === 'added') {
                status = 'created'
              } else {
                status = 'deleted'
              }
            }
            const selectedViewDefaultTitle = getViewDefaultTitle(selectedView)

            return (
              <div key={view.id} className="relative">
                <TabsTrigger
                  data-view-id={view.id}
                  value={view.id}
                  asChild
                  className="[radix-state-active]:!font-normal transition-none"
                >
                  <Button
                    unstyled
                    className="group radix-state-active:text-dark-bright text-dark-muted focus-visible:outline-none focus:outline-none !rounded-none text-xs group relative cursor-default !min-w-0 !p-0"
                  >
                    <div className="group-focus-visible:outline group-focus-visible:outline-1 group-focus-visible:outline-dark-control-base group-focus-visible:-outline-offset-4 rounded-lg flex items-center gap-1.5 px-3 py-1.5">
                      <ListBlockViewIcon
                        view={view}
                        iconProps={{ width: 16, height: 16 }}
                      />
                      <span className="group-radix-state-active:font-medium whitespace-nowrap">
                        {view.title ||
                          (view.id === selectedView?.id
                            ? selectedViewDefaultTitle
                            : getViewDefaultTitle(view))}
                      </span>

                      {status !== 'unmodified' && (
                        <StatusIcon
                          status={status}
                          className="absolute top-0.5 left-1 size-[14px]"
                        />
                      )}

                      <span className="w-full absolute bottom-0 left-0 group-radix-state-active:bg-dark-control-base bg-transparent h-[1px]" />
                    </div>
                  </Button>
                </TabsTrigger>
              </div>
            )
          })}
        </TabsList>
      </Tabs>

      <div className="my-3 space-y-1">
        <p>
          <span className="text-dark-muted font-medium">Filters:</span>&nbsp;
          <InlineTextDiff
            className={clsx(
              (condition === 'removed' || !currentFilters) &&
                '!text-dark-muted',
              'py-2 !h-auto min-h-[39px]'
            )}
            text={currentFilters || 'Unset'}
            textMatch={oppositeFilters || 'Unset'}
            condition={condition}
            // isConflictingText={isConflictOnVariants}
            invertBasedOnCondition
          />
        </p>
        <p>
          <span className="text-dark-muted font-medium">Sort:</span>&nbsp;
          <InlineTextDiff
            className={clsx(
              (condition === 'removed' || !currentSort) && '!text-dark-muted',
              'py-2 !h-auto min-h-[39px]'
            )}
            text={currentSort || 'Unset'}
            textMatch={oppositeSort || 'Unset'}
            condition={condition}
            // isConflictingText={isConflictOnVariants}
            invertBasedOnCondition
          />
        </p>
      </div>
    </>
  )
}
